import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import NotificationHistory from "./components/NotificationHistory";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SessionTimeoutProvider } from "./components/SessionTimeoutProvider";
import { UploadProvider } from "./contexts/UploadContext";
import UploadWizard from "./pages/UploadWizard";
import ProviderARoll from "./pages/ProviderARoll";
import Confirmation from "./pages/Confirmation";
import TagLibrary from "./pages/TagLibrary";
import FootageSearch from "./pages/FootageSearch";
import Recents from "./pages/Recents";
import LibraryStats from "./pages/LibraryStats";
import AuditLog from "./pages/AuditLog";
import Reports from "./pages/Reports";
import WeeklyClips from "./pages/WeeklyClips";
import WallDisplay from "./pages/WallDisplay";
import AuthorizedAccounts from "./pages/AuthorizedAccounts";
import GDriveSuccess from "./pages/GDriveSuccess";
import Guide from "./pages/Guide";
import Boards from "./pages/Boards";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";

// ── Per-page browser tab titles ──────────────────────────────────────────
// Keeps the suffix in sync with whatever page the user is on, so multiple
// open tabs are individually identifiable. Labels mirror the nav-tab text in
// AppHeader where applicable; admin-only and auxiliary pages get their own
// short labels.
const PAGE_TITLES: Record<string, string> = {
  "/": "Patient Footage",
  "/provider-a-roll": "Provider A-Roll",
  "/confirmation": "Confirmation",
  "/tags": "Tag Libraries",
  "/boards": "Boards",
  "/footage-search": "Footage Search",
  "/recents": "Recents",
  "/library": "Footage Search",
  "/library-stats": "Library Stats",
  "/audit": "Audit Log",
  "/reports": "Reports",
  "/weekly-clips": "Weekly Clips",
  "/authorized-accounts": "Authorized Accounts",
  "/guide": "Guide",
  "/gdrive-success": "Drive Connected",
  "/login": "Sign In",
  "/change-password": "Change Password",
  "/404": "Not Found",
};

function labelForPath(path: string): string {
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  // Fallback: title-case the first path segment ("/foo-bar/baz" → "Foo Bar").
  const seg = path.replace(/^\/+/, "").split("/")[0];
  if (!seg) return "Home";
  return seg
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function DocumentTitle() {
  const [location] = useLocation();
  useEffect(() => {
    document.title = `Footage Manager - ${labelForPath(location)}`;
  }, [location]);
  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/change-password" component={ChangePassword} />
      <Route path="/" component={UploadWizard} />
      <Route path="/provider-a-roll" component={ProviderARoll} />
      <Route path="/confirmation" component={Confirmation} />
      <Route path="/tags" component={TagLibrary} />
      <Route path="/boards" component={Boards} />
      <Route path="/footage-search" component={FootageSearch} />
      <Route path="/recents" component={Recents} />
      {/* Back-compat: old /library bookmarks redirect to the renamed route. */}
      <Route path="/library">
        <Redirect to="/footage-search" />
      </Route>
      <Route path="/audit" component={AuditLog} />
      <Route path="/reports" component={Reports} />
      <Route path="/weekly-clips" component={WeeklyClips} />
      {/* Login-free wall display link — see pages/WallDisplay.tsx. */}
      <Route path="/wall/:slug" component={WallDisplay} />
      <Route path="/authorized-accounts" component={AuthorizedAccounts} />
      <Route path="/library-stats" component={LibraryStats} />
      <Route path="/guide" component={Guide} />
      <Route path="/gdrive-success" component={GDriveSuccess} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          {/* Bump Sonner's default bottom offset so the notification bell
              (fixed bottom-right at bottom-4 right-4) doesn't visually
              collide with incoming toasts. Bell is 11 tall + 4 of bottom
              gap → toasts stack above ~5rem off the bottom. */}
          <Toaster offset={{ bottom: "5rem", right: "1rem" }} />
          <UploadProvider>
            <SessionTimeoutProvider>
              <DocumentTitle />
              <Router />
              <NotificationHistory />
            </SessionTimeoutProvider>
          </UploadProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
